import React, { useCallback, useEffect, useState } from 'react';
import { Table, Modal, Input, Tooltip, Empty, Select } from 'antd';
import { Landmark, RefreshCw, UserPlus, KeyRound, Power, Copy, ExternalLink, ShieldCheck, History, Eye } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../config/apiConfig';
import { useToastMessage } from '../contexts/ToastMessageContext.jsx';

const SITE_URL = 'https://dashboard.kairos-global-k.com';
const btn = 'inline-flex !h-8 !min-h-0 items-center gap-1.5 rounded-md !px-3 !py-0 text-xs font-semibold disabled:opacity-50';
const fmt = (iso) => (iso ? new Date(iso).toLocaleString('ko-KR', { timeZone: 'Asia/Seoul', year: '2-digit', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—');
const usd = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(Number(n || 0));
const errMsg = (e) => e?.response?.data?.code || e?.response?.data?.message || e?.message || 'error';

const GovAccessManagementPage = () => {
  const { t: tr } = useTranslation();
  const T = (k, o) => tr(`govAccess.${k}`, o);
  const { showToastMessage } = useToastMessage();
  const toast = (m, type = 'success') => showToastMessage?.(m, type);

  const [tab, setTab] = useState('accounts');
  const [viewers, setViewers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ email: '', name: '', organization: 'Ministry of Labour (Myanmar)' });
  const [saving, setSaving] = useState(false);
  const [secret, setSecret] = useState(null);
  const [logFilter, setLogFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [v, l] = await Promise.all([
        apiClient.get('/api/gov/admin/viewers'),
        apiClient.get('/api/gov/admin/access-logs', { params: { limit: 300 } }),
      ]);
      setViewers(v.data.viewers || []);
      setLogs(l.data.logs || []);
    } catch (e) {
      toast(T('msg.loadFailed', { error: errMsg(e) }), 'error');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPreview = useCallback(async (fresh = false) => {
    try {
      const res = await apiClient.get('/api/gov/admin/preview', { params: { range: '30', fresh: fresh ? '1' : '0' } });
      setPreview(res.data);
    } catch (e) {
      toast(T('msg.previewFailed', { error: errMsg(e) }), 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (tab === 'preview' && !preview) loadPreview(); }, [tab, preview, loadPreview]);

  const copy = (text) => {
    navigator.clipboard?.writeText(text);
    toast(T('msg.copied'));
  };

  const create = async () => {
    if (!form.email.trim()) return toast(T('form.emailRequired'), 'error');
    setSaving(true);
    try {
      const res = await apiClient.post('/api/gov/admin/viewers', form);
      setCreateOpen(false);
      setSecret({ email: res.data.viewer.email, password: res.data.tempPassword, kind: 'created' });
      setForm({ email: '', name: '', organization: form.organization });
      await load();
    } catch (e) {
      toast(T(`code.${errMsg(e)}`, { defaultValue: T('msg.createFailed', { error: errMsg(e) }) }), 'error');
    } finally {
      setSaving(false);
    }
    return undefined;
  };

  const resetPassword = (v) => {
    Modal.confirm({
      title: T('reset.title'),
      content: T('reset.body', { email: v.email }),
      okText: T('reset.ok'),
      cancelText: T('common.cancel'),
      onOk: async () => {
        try {
          const res = await apiClient.post(`/api/gov/admin/viewers/${v.id}/reset-password`);
          setSecret({ email: v.email, password: res.data.tempPassword, kind: 'reset' });
          await load();
        } catch (e) {
          toast(T('msg.actionFailed', { error: errMsg(e) }), 'error');
        }
      },
    });
  };

  const toggleActive = (v) => {
    Modal.confirm({
      title: v.active ? T('disable.title') : T('enable.title'),
      content: v.active ? T('disable.body', { email: v.email }) : T('enable.body', { email: v.email }),
      okText: v.active ? T('disable.ok') : T('enable.ok'),
      okButtonProps: { danger: v.active },
      cancelText: T('common.cancel'),
      onOk: async () => {
        try {
          await apiClient.post(`/api/gov/admin/viewers/${v.id}/active`, { active: !v.active });
          toast(v.active ? T('msg.disabled') : T('msg.enabled'));
          await load();
        } catch (e) {
          toast(T('msg.actionFailed', { error: errMsg(e) }), 'error');
        }
      },
    });
  };

  const statusOf = (v) => {
    if (!v.active) return ['bg-slate-100 text-slate-500', T('status.disabled')];
    if (v.lockedUntil) return ['bg-rose-50 text-rose-700', T('status.locked')];
    if (v.mustChangePassword) return ['bg-amber-50 text-amber-700', T('status.pending')];
    return ['bg-emerald-50 text-emerald-700', T('status.active')];
  };

  const viewerCols = [
    { title: T('col.account'), key: 'a', render: (_, v) => (
      <div>
        <div className="text-xs font-semibold text-slate-800">{v.name || v.email}</div>
        <div className="text-[11px] text-slate-400">{v.email}</div>
      </div>
    ) },
    { title: T('col.organization'), dataIndex: 'organization', render: (o) => <span className="text-xs text-slate-600">{o || '—'}</span> },
    { title: T('col.status'), key: 's', width: 120, render: (_, v) => { const [cls, label] = statusOf(v); return <span className={`rounded px-1.5 py-0.5 text-[11px] font-semibold ${cls}`}>{label}</span>; } },
    { title: T('col.lastLogin'), dataIndex: 'lastLoginAt', width: 130, render: (d) => <span className="text-[11px] tabular-nums text-slate-500">{fmt(d)}</span> },
    { title: T('col.created'), dataIndex: 'createdAt', width: 130, render: (d) => <span className="text-[11px] tabular-nums text-slate-500">{fmt(d)}</span> },
    { title: '', key: 'x', width: 90, render: (_, v) => (
      <div className="flex justify-end gap-1">
        <Tooltip title={T('action.reset')}>
          <button type="button" onClick={() => resetPassword(v)} className="rounded p-1.5 text-sky-600 hover:bg-sky-50"><KeyRound size={14} /></button>
        </Tooltip>
        <Tooltip title={v.active ? T('action.disable') : T('action.enable')}>
          <button type="button" onClick={() => toggleActive(v)} className={`rounded p-1.5 ${v.active ? 'text-rose-600 hover:bg-rose-50' : 'text-emerald-600 hover:bg-emerald-50'}`}><Power size={14} /></button>
        </Tooltip>
      </div>
    ) },
  ];

  const ACTIONS = ['login', 'login_failed', 'dashboard_view', 'password_changed', 'password_reset', 'account_created', 'account_disabled', 'account_enabled'];
  const filteredLogs = logs.filter((l) => logFilter === 'all' || l.action === logFilter);
  const logCols = [
    { title: T('col.time'), dataIndex: 'createdAt', width: 130, render: (d) => <span className="text-[11px] tabular-nums text-slate-500">{fmt(d)}</span> },
    { title: T('col.action'), dataIndex: 'action', width: 150, render: (a) => (
      <span className={`rounded px-1.5 py-0.5 text-[11px] font-semibold ${a === 'login_failed' ? 'bg-rose-50 text-rose-700' : a === 'dashboard_view' ? 'bg-sky-50 text-sky-700' : 'bg-slate-100 text-slate-600'}`}>{T(`log.${a}`, { defaultValue: a })}</span>
    ) },
    { title: T('col.account'), dataIndex: 'email', render: (e) => <span className="text-xs text-slate-700">{e || '—'}</span> },
    { title: T('col.detail'), key: 'd', render: (_, l) => (
      <span className="text-[11px] text-slate-500">
        {[l.range ? T('log.range', { r: l.range }) : null, l.reason ? T(`reason.${l.reason}`, { defaultValue: l.reason }) : null, l.adminUid ? T('log.byAdmin') : null].filter(Boolean).join(' · ') || '—'}
      </span>
    ) },
    { title: 'IP', dataIndex: 'ip', width: 130, render: (ip) => <span className="font-mono text-[11px] text-slate-500">{ip || '—'}</span> },
  ];

  const lastView = logs.find((l) => l.action === 'dashboard_view');
  const failed24h = logs.filter((l) => l.action === 'login_failed' && l.createdAt && Date.now() - new Date(l.createdAt).getTime() < 864e5).length;

  return (
    <div className="min-h-screen bg-slate-50 p-3">
      <div className="mb-3 rounded-lg border border-slate-200 bg-white">
        <div className="flex flex-wrap items-start justify-between gap-3 px-4 pt-3">
          <div>
            <h1 className="flex items-center gap-2 text-base font-bold text-slate-900"><Landmark size={18} className="text-amber-600" />{T('title')}</h1>
            <p className="mt-0.5 text-xs text-slate-500">{T('subtitle')}</p>
            <div className="mt-1.5 flex flex-wrap gap-1.5 text-[11px]">
              <span className="rounded bg-emerald-50 px-2 py-0.5 font-medium text-emerald-700">{T('chip.active', { n: viewers.filter((v) => v.active).length })}</span>
              <span className="rounded bg-slate-100 px-2 py-0.5 font-medium text-slate-600">{T('chip.lastView', { time: fmt(lastView?.createdAt) })}</span>
              {failed24h ? <span className="rounded bg-rose-50 px-2 py-0.5 font-medium text-rose-700">{T('chip.failed', { n: failed24h })}</span> : null}
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5">
            <button type="button" onClick={() => copy(SITE_URL)} className={`${btn} border border-slate-300 bg-white text-slate-700 hover:bg-slate-100`}><Copy size={13} />{SITE_URL.replace('https://', '')}</button>
            <a href={SITE_URL} target="_blank" rel="noreferrer" className={`${btn} border border-slate-300 bg-white text-slate-700 hover:bg-slate-100`}><ExternalLink size={13} />{T('action.openSite')}</a>
            <button type="button" onClick={() => setCreateOpen(true)} className={`${btn} bg-slate-900 text-white hover:bg-slate-800`}><UserPlus size={13} />{T('action.create')}</button>
            <button type="button" onClick={load} disabled={loading} className={`${btn} border border-slate-300 bg-white text-slate-700 hover:bg-slate-100`}><RefreshCw size={13} className={loading ? 'animate-spin' : ''} /></button>
          </div>
        </div>
        <div className="mx-4 my-3 flex gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-[11px] leading-relaxed text-emerald-800">
          <ShieldCheck size={14} className="mt-0.5 shrink-0" />{T('scope')}
        </div>
        <div className="flex gap-1 border-t border-slate-100 px-3">
          {[['accounts', T('tab.accounts'), UserPlus], ['logs', T('tab.logs'), History], ['preview', T('tab.preview'), Eye]].map(([k, label, Icon]) => (
            <button key={k} type="button" onClick={() => setTab(k)}
              className={`flex items-center gap-1.5 border-b-2 px-3 py-2 text-xs font-semibold ${tab === k ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
              <Icon size={13} />{label}
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white">
        {tab === 'accounts' ? (
          <Table size="small" rowKey="id" loading={loading} dataSource={viewers} columns={viewerCols} pagination={false} scroll={{ x: 800 }}
            locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={T('empty.accounts')} /> }} />
        ) : null}
        {tab === 'logs' ? (
          <>
            <div className="border-b border-slate-100 bg-slate-50 px-3 py-2">
              <Select size="small" value={logFilter} onChange={setLogFilter} className="!w-52"
                options={[{ value: 'all', label: T('log.all') }, ...ACTIONS.map((a) => ({ value: a, label: T(`log.${a}`) }))]} />
            </div>
            <Table size="small" rowKey="id" loading={loading} dataSource={filteredLogs} columns={logCols} scroll={{ x: 800 }}
              pagination={{ pageSize: 30, size: 'small' }}
              locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={T('empty.logs')} /> }} />
          </>
        ) : null}
        {tab === 'preview' ? (
          <div className="p-4">
            <div className="mb-3 flex items-center justify-between">
              <p className="text-xs text-slate-500">{T('preview.hint')}</p>
              <button type="button" onClick={() => loadPreview(true)} className={`${btn} border border-slate-300 bg-white text-slate-700`}><RefreshCw size={13} />{T('preview.recompute')}</button>
            </div>
            {preview ? (
              <>
                <div className="grid grid-cols-2 gap-2 md:grid-cols-3 xl:grid-cols-6">
                  {[
                    [T('preview.total'), usd(preview.kpis?.totalCompletedUSD)],
                    [T('preview.today'), usd(preview.kpis?.todayCompletedUSD)],
                    [T('preview.workers'), Number(preview.kpis?.registeredUsers || 0).toLocaleString()],
                    [T('preview.compliance'), `${preview.compliance?.averageCompliance ?? 0}% (${preview.compliance?.compliant ?? 0}/${preview.compliance?.total ?? 0})`],
                    [T('preview.success'), `${preview.kpis?.successRate ?? 0}%`],
                    [T('preview.avg'), preview.kpis?.avgCompletionHours == null ? '—' : `${preview.kpis.avgCompletionHours}h`],
                  ].map(([k, v]) => (
                    <div key={k} className="rounded-md border border-slate-200 px-3 py-2">
                      <div className="text-[11px] text-slate-500">{k}</div>
                      <div className="text-base font-bold tabular-nums text-slate-900">{v}</div>
                    </div>
                  ))}
                </div>
                <details className="mt-3">
                  <summary className="cursor-pointer text-xs font-semibold text-slate-600">{T('preview.raw')}</summary>
                  <pre className="mt-2 max-h-96 overflow-auto rounded bg-slate-900 p-3 text-[11px] text-slate-100">{JSON.stringify(preview, null, 2)}</pre>
                </details>
                <p className="mt-2 text-[11px] text-slate-400">{T('preview.generated', { time: fmt(preview.generatedAt) })}</p>
              </>
            ) : <div className="py-10 text-center text-xs text-slate-400">…</div>}
          </div>
        ) : null}
      </div>

      <Modal open={createOpen} title={T('form.title')} onCancel={() => setCreateOpen(false)} onOk={create} confirmLoading={saving}
        okText={T('form.submit')} cancelText={T('common.cancel')} destroyOnHidden>
        <div className="space-y-3">
          {[['email', T('form.email'), 'officer@mol.gov.mm'], ['name', T('form.name'), 'U Aung Aung'], ['organization', T('form.organization'), 'Ministry of Labour (Myanmar)']].map(([key, label, ph]) => (
            <label key={key} className="block text-xs font-medium text-slate-600">
              {label}
              <Input className="mt-1" value={form[key]} placeholder={ph} onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))} />
            </label>
          ))}
          <p className="rounded-md bg-slate-50 px-3 py-2 text-[11px] leading-relaxed text-slate-500">{T('form.hint')}</p>
        </div>
      </Modal>

      <Modal open={!!secret} title={secret?.kind === 'reset' ? T('secret.resetTitle') : T('secret.createdTitle')} onCancel={() => setSecret(null)}
        footer={<button type="button" onClick={() => setSecret(null)} className={`${btn} bg-slate-900 text-white`}>{T('secret.done')}</button>}>
        {secret ? (
          <div className="space-y-3 text-xs">
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-800">{T('secret.warning')}</div>
            {[[T('secret.site'), SITE_URL], [T('form.email'), secret.email], [T('secret.password'), secret.password]].map(([k, v]) => (
              <div key={k} className="flex items-center justify-between gap-2 rounded-md border border-slate-200 px-3 py-2">
                <div><div className="text-[11px] text-slate-400">{k}</div><div className="font-mono text-sm font-semibold text-slate-900">{v}</div></div>
                <button type="button" onClick={() => copy(v)} className="rounded p-1.5 text-slate-500 hover:bg-slate-100"><Copy size={14} /></button>
              </div>
            ))}
            <button type="button" onClick={() => copy(T('secret.message', { site: SITE_URL, email: secret.email, password: secret.password }))}
              className={`${btn} w-full justify-center border border-slate-300 bg-white text-slate-700`}><Copy size={13} />{T('secret.copyAll')}</button>
          </div>
        ) : null}
      </Modal>
    </div>
  );
};

export default GovAccessManagementPage;
